import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { Toast } from 'primereact/toast';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RegisterSchoolAdminModalProps {
  visible: boolean;
  onHide: () => void;
  onSuccess: () => void;
  schoolIdCode: string;
  schoolName: string;
  toast: React.RefObject<Toast | null>;
}

interface AdminFormData {
  admin_login: string;
  admin_password: string;
  admin_email: string;
  admin_display_name: string;
}

const INITIAL_FORM: AdminFormData = {
  admin_login: '',
  admin_password: '',
  admin_email: '',
  admin_display_name: '',
};

// ── Sub-components (matches SchoolModal) ────────────────────────────────────────

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label style={{ display: 'block', marginBottom: '7px', color: '#374151', fontWeight: '600', fontSize: '13px', letterSpacing: '0.01em' }}>
    {children}{required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
  </label>
);

const TextInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    style={{
      width: '100%',
      padding: '10px 13px',
      border: '1.5px solid #e5e7eb',
      borderRadius: '9px',
      fontSize: '14px',
      color: '#111827',
      background: '#fff',
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 0.18s',
      ...props.style,
    }}
    onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; props.onFocus?.(e); }}
    onBlur={(e)  => { e.currentTarget.style.borderColor = '#e5e7eb'; props.onBlur?.(e); }}
  />
);

// ── Main Component ─────────────────────────────────────────────────────────────

export default function RegisterSchoolAdminModal({
  visible,
  onHide,
  onSuccess,
  schoolIdCode,
  schoolName,
  toast,
}: RegisterSchoolAdminModalProps) {
  const [formData, setFormData] = useState<AdminFormData>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (visible) {
      setFormData(INITIAL_FORM);
      setError(null);
      setShowPassword(false);
    }
  }, [visible]);

  const handleChange = (field: keyof AdminFormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = (): void => {
    onHide();
  };

  const handleSubmit = async (): Promise<void> => {
    const { admin_display_name, admin_login, admin_email, admin_password } = formData;

    if (!admin_display_name.trim() || !admin_login.trim() || !admin_email.trim() || !admin_password) {
      setError('Please fill all required fields.');
      return;
    }
    if (admin_password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post('/register-school-admin', {
        school_id_code: schoolIdCode,
        admin_login,
        admin_password,
        admin_email,
        admin_display_name,
      });

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Admin registered for ${schoolName}`,
          life: 4000,
        });
        handleClose();
        onSuccess();
      } else {
        setError(response.data.message || 'Failed to register admin.');
      }
    } catch (err: any) {
      const status = err.response?.status;
      const body = err.response?.data;

      if (status === 401) setError('Authentication failed. Please login again.');
      else if (status === 403) setError('You do not have permission to perform this action.');
      else if (status === 409) setError(body?.error || 'An admin with this username or email already exists.');
      else if (status === 400) setError(body?.error || body?.details || 'Invalid data provided.');
      else setError(body?.message || err.message || 'Failed to register admin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalHeader = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        background: 'rgba(255,255,255,0.2)',
        borderRadius: '10px', padding: '8px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className="pi pi-user-plus" style={{ fontSize: '18px', color: 'white' }} />
      </div>
      <div>
        <div style={{ color: 'white', fontWeight: '700', fontSize: '17px', lineHeight: 1.2 }}>
          Register Admin
        </div>
        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '2px' }}>
          {schoolName}
          {schoolIdCode && (
            <span style={{
              marginLeft: '8px',
              fontFamily: 'monospace',
              background: 'rgba(255,255,255,0.15)',
              padding: '1px 8px',
              borderRadius: '6px',
              fontSize: '11px',
            }}>
              {schoolIdCode}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .p-dialog.admin-reg-unified-modal .p-dialog-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #2855ae 60%, #3b82f6 100%) !important;
          color: white !important;
          border-radius: 16px 16px 0 0 !important;
          padding: 18px 24px !important;
        }
        .p-dialog.admin-reg-unified-modal .p-dialog-header .p-dialog-header-icon {
          color: rgba(255,255,255,0.7) !important;
        }
        .p-dialog.admin-reg-unified-modal .p-dialog-header .p-dialog-header-icon:hover {
          color: white !important;
          background: rgba(255,255,255,0.15) !important;
        }
        .p-dialog.admin-reg-unified-modal .p-dialog-content {
          padding: 0 !important;
          border-radius: 0 0 16px 16px !important;
          overflow: hidden;
        }
        .p-dialog.admin-reg-unified-modal {
          border-radius: 16px !important;
          box-shadow: 0 24px 64px rgba(0,0,0,0.18) !important;
        }
      `}</style>

      <Dialog
        header={modalHeader}
        visible={visible}
        onHide={handleClose}
        style={{ width: '480px', maxWidth: '94vw' }}
        draggable={false}
        resizable={false}
        className="admin-reg-unified-modal"
        footer={null}
      >
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: '72vh' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            <div>
              <FieldLabel>School Code</FieldLabel>
              <TextInput
                type="text"
                value={schoolIdCode}
                disabled
                style={{ background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }}
              />
            </div>

            <div>
              <FieldLabel required>Admin Full Name</FieldLabel>
              <TextInput
                type="text"
                value={formData.admin_display_name}
                onChange={(e) => handleChange('admin_display_name', e.target.value)}
                placeholder="e.g., Rajesh Kumar"
              />
            </div>

            <div>
              <FieldLabel required>Admin Username</FieldLabel>
              <TextInput
                type="text"
                value={formData.admin_login}
                onChange={(e) => handleChange('admin_login', e.target.value)}
                placeholder="e.g., gv_admin"
              />
            </div>

            <div>
              <FieldLabel required>Admin Email</FieldLabel>
              <TextInput
                type="email"
                value={formData.admin_email}
                onChange={(e) => handleChange('admin_email', e.target.value)}
                placeholder="e.g., admin@greenvalley.com"
              />
            </div>

            <div>
              <FieldLabel required>Admin Password</FieldLabel>
              <div style={{ position: 'relative' }}>
                <TextInput
                  type={showPassword ? 'text' : 'password'}
                  value={formData.admin_password}
                  onChange={(e) => handleChange('admin_password', e.target.value)}
                  placeholder="Minimum 6 characters"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
                    display: 'flex', alignItems: 'center', padding: '2px',
                  }}
                  tabIndex={-1}
                >
                  <i className={`pi ${showPassword ? 'pi-eye-slash' : 'pi-eye'}`} style={{ fontSize: '15px' }} />
                </button>
              </div>
            </div>

            {error && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: '9px', padding: '11px 14px',
                color: '#dc2626', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <i className="pi pi-exclamation-circle" />
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                style={{
                  flex: 1, padding: '12px',
                  background: '#f3f4f6', border: '1.5px solid #e5e7eb',
                  borderRadius: '10px', cursor: 'pointer',
                  color: '#374151', fontSize: '14px', fontWeight: '600',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e5e7eb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{
                  flex: 2, padding: '12px',
                  background: isSubmitting ? '#93c5fd' : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  border: 'none', borderRadius: '10px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  color: 'white', fontSize: '14px', fontWeight: '600',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(59,130,246,0.35)',
                  transition: 'all 0.15s',
                }}
              >
                {isSubmitting ? (
                  <><i className="pi pi-spin pi-spinner" style={{ fontSize: '14px' }} /> Registering...</>
                ) : (
                  <><i className="pi pi-user-plus" style={{ fontSize: '14px' }} /> Register Admin</>
                )}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}